/**
 * Камера модератора: кто вещает и как разобрать её кадр.
 *
 * ЧТО ЭТО. Модератор выбирает камеру своей машины — вебку,
 * виртуальную камеру OBS, что угодно из списка устройств, — и она
 * встаёт в кадр слоем, который двигают и масштабируют, как всякий
 * другой. Кадры идут тем же путём, что звук эфира: панель кодирует
 * WebCodecs, hub раздаёт комнате (обоснование в services/hub —
 * gateway/camera.go).
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Ни декодера, ни отрисовки: здесь только разбор
 * того, что пришло по сети. Разбор общий для панели и оверлея —
 * камеру видят обе стороны, и вторая реализация разошлась бы
 * с первой молча.
 */
/** Версия формата кадра камеры. Первый байт. */
import { decodeMessage } from './message.js';
export const CAMERA_VERSION = 2;
/** Признак ключевого кадра во втором байте. */
export const CAMERA_FLAG_KEYFRAME = 1;
/** Версия, признаки, номер кадра и длина имени источника. */
export const CAMERA_FIXED_HEADER_BYTES = 7;
/**
 * Разбирает сообщение `cameras` — список вещающих.
 *
 * Списком целиком, а не «этот включился»: получатель, потерявший
 * одно сообщение, иначе остался бы с неверным набором навсегда,
 * а список чинит себя сам на следующем же изменении.
 */
export function parseCameras(raw) {
    const message = decodeMessage(raw);
    if (message === null || message.t !== 'cameras')
        return null;
    const sources = message.sources;
    if (!Array.isArray(sources))
        return null;
    const out = [];
    for (const item of sources) {
        const source = readSource(item);
        // Негодный источник пропускается, а не рушит весь список:
        // из-за одной кривой камеры не должны пропасть остальные
        if (source !== null)
            out.push(source);
    }
    return out;
}
function readSource(raw) {
    const item = decodeMessage(raw);
    if (item === null)
        return null;
    const clientId = item.clientId;
    if (typeof clientId !== 'string' || clientId === '')
        return null;
    const config = decodeMessage(item.config);
    if (config === null)
        return null;
    const codec = config.codec;
    if (typeof codec !== 'string' || codec === '')
        return null;
    const width = size(config.width);
    const height = size(config.height);
    if (width === null || height === null)
        return null;
    const description = typeof config.description === 'string' ? config.description : '';
    return {
        clientId,
        userId: typeof item.userId === 'string' ? item.userId : '',
        userName: typeof item.userName === 'string' ? item.userName : '',
        config: { codec, width, height, description },
    };
}
/**
 * Размер стороны кадра.
 *
 * Проверяется строго: число уезжает в конструктор декодера в чужом
 * браузере, и негодное там — исключение посреди эфира, а не мутная
 * картинка.
 */
function size(value) {
    if (typeof value !== 'number' || !Number.isInteger(value))
        return null;
    return value >= 16 && value <= 3840 ? value : null;
}
/**
 * Разбирает двоичный кадр камеры.
 *
 * Возвращает `null` на чужой версии — в том числе на кадре звука,
 * помеченном единицей. Это и есть смысл версии в заголовке:
 * получатель молча выбрасывает то, чего не понимает, вместо того
 * чтобы скормить чужие байты своему декодеру.
 */
export function decodeCameraFrame(data) {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    if (bytes.length <= CAMERA_FIXED_HEADER_BYTES)
        return null;
    if (bytes[0] !== CAMERA_VERSION)
        return null;
    const nameLength = bytes[6] ?? 0;
    if (nameLength === 0)
        return null;
    const start = CAMERA_FIXED_HEADER_BYTES + nameLength;
    if (bytes.length <= start)
        return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return {
        source: new TextDecoder().decode(bytes.subarray(CAMERA_FIXED_HEADER_BYTES, start)),
        seq: view.getUint32(2),
        keyframe: ((bytes[1] ?? 0) & CAMERA_FLAG_KEYFRAME) !== 0,
        payload: bytes.subarray(start),
    };
}
//# sourceMappingURL=camera.js.map